import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Star, Award, Plus, Minus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "./engShared";
import EngRedemptions from "./EngRedemptions";
import EngAnnouncements from "./EngAnnouncements";

const POINT_LABELS = {
  song_request: "Song request", dedication: "Dedication", message: "Message",
  poll_vote: "Poll vote (claimed)", contest_entry: "Contest entry",
};

export default function EngRewards() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/engagement/rewards").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;

  const save = async (patch) => {
    const next = { ...data, ...patch, points: { ...data.points, ...(patch.points || {}) } };
    setData(next);
    try { await api.put("/engagement/rewards", { enabled: next.enabled, points: next.points }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const adjust = async (listener_id, delta) => {
    setBusy(listener_id);
    try {
      await api.post("/engagement/rewards/adjust", { listener_id, delta, reason: "manual adjustment" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjust failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="eng-rewards-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Rewards & Loyalty</h1>
        <p className="text-sm text-ink-secondary">Listeners earn points and badges for taking part. Points are a loyalty mechanic — never cash value.</p>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-rewards-config">
        <label className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm font-bold"><Crown className="h-4 w-4 text-brand-amber" /> Rewards program</span>
          <input type="checkbox" checked={!!data.enabled} data-testid="eng-rewards-enabled-toggle"
            onChange={(e) => save({ enabled: e.target.checked })} />
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(POINT_LABELS).map(([k, label]) => (
            <div key={k}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">{label}</div>
              <input type="number" min={0} max={1000} value={data.points?.[k] ?? 0} data-testid={`eng-rewards-pts-${k}`}
                onChange={(e) => save({ points: { [k]: Number(e.target.value) } })}
                className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm" />
            </div>
          ))}
        </div>
        <div className="text-[11px] text-ink-muted">Points are awarded automatically when listeners include their email. Total awarded so far: <span className="text-brand-amber font-bold" data-testid="eng-rewards-total">{data.total_points_awarded}</span></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-rewards-leaderboard">
          <div className="flex items-center gap-2 text-sm font-bold mb-3"><Award className="h-4 w-4 text-brand-cyan" /> Leaderboard</div>
          {!data.leaderboard?.length && <div className="text-xs text-ink-muted">No points earned yet — enable the program and share your listener page.</div>}
          {(data.leaderboard || []).map((l, i) => (
            <div key={l.listener_id} className="flex items-center gap-3 border-b border-line/50 py-2 last:border-0" data-testid={`eng-lb-row-${l.listener_id}`}>
              <span className={`font-mono text-xs w-6 text-center ${i < 3 ? "text-brand-amber font-bold" : "text-ink-muted"}`}>#{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                  {l.name || l.email}
                  {l.vip && <Star className="h-3 w-3 text-brand-amber fill-brand-amber" />}
                  {(l.badges || []).map((b) => (
                    <span key={b.key} title={b.desc} className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan px-1.5 py-0.5 text-[9px] font-mono uppercase">{b.label}</span>
                  ))}
                </div>
                <div className="text-[10px] text-ink-muted">{l.counts?.total || 0} interactions</div>
              </div>
              <span className="font-display font-bold text-brand-amber" data-testid={`eng-lb-pts-${l.listener_id}`}>{l.rewards_points} pts</span>
              <div className="flex gap-1">
                <button onClick={() => adjust(l.listener_id, 25)} disabled={busy === l.listener_id} data-testid={`eng-lb-plus-${l.listener_id}`}
                  className="rounded border border-line px-1.5 py-0.5 text-[10px] text-emerald-300" title="+25 points"><Plus className="h-3 w-3" /></button>
                <button onClick={() => adjust(l.listener_id, -25)} disabled={busy === l.listener_id} data-testid={`eng-lb-minus-${l.listener_id}`}
                  className="rounded border border-line px-1.5 py-0.5 text-[10px] text-rose-300" title="-25 points"><Minus className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-rewards-badges">
            <div className="text-sm font-bold mb-2">Badges</div>
            {(data.badge_defs || []).map((b) => (
              <div key={b.key} className="flex items-center justify-between py-1 text-xs">
                <span className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan px-2 py-0.5 text-[10px] font-mono uppercase">{b.label}</span>
                <span className="text-[10px] text-ink-muted">{b.desc}</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-rewards-ledger">
            <div className="text-sm font-bold mb-2">Recent activity</div>
            {!data.ledger?.length && <div className="text-xs text-ink-muted">Nothing yet.</div>}
            {(data.ledger || []).map((r) => (
              <div key={r.ledger_id} className="flex items-center justify-between py-1 text-[11px] border-b border-line/40 last:border-0">
                <span className="text-ink-secondary">{(r.activity || "").replace("_", " ")}</span>
                <span className={`font-mono ${r.points >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.points >= 0 ? "+" : ""}{r.points}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <EngRedemptions />

      <EngAnnouncements />
    </div>
  );
}
